// Shared helpers for the Gift Card Store: cart (per currency), money format, country guess, card tile.
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";

const KEY = "g2c_store_cart_v2";
const EVT = "g2c-store-cart";
export const SYM = { CAD: "CA$", USD: "US$" };
export const COUNTRY_CUR = { CA: "CAD", US: "USD" };

export function money(cur, v) {
    const n = Number(v || 0);
    return `${SYM[cur] || "$"}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function shortMoney(cur, v) {
    const n = Number(v || 0);
    return `${SYM[cur] || "$"}${Number.isInteger(n) ? n : n.toFixed(2)}`;
}

export function guessCountry() {
    try {
        const saved = localStorage.getItem("g2c_country");
        if (saved === "us" || saved === "ca") return saved.toUpperCase();
    } catch { /* ignore */ }
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|St_Johns|Regina|Moncton|Montreal|Whitehorse|Yellowknife|Iqaluit|Glace_Bay|Goose_Bay|Swift_Current|Dawson_Creek|Fort_Nelson|Creston|Atikokan|Rankin_Inlet|Cambridge_Bay|Inuvik|Dawson|Blanc-Sablon)$/.test(tz)) return "CA";
        if (/-CA$/i.test(navigator.language || "")) return "CA";
        if (/^America\//.test(tz)) return "US";
    } catch { /* ignore */ }
    return "CA";
}

export function saveCountry(c) {
    try { localStorage.setItem("g2c_country", (c || "CA").toLowerCase()); } catch { /* ignore */ }
}

function read() {
    try {
        const d = JSON.parse(localStorage.getItem(KEY) || "{}");
        return { CAD: Array.isArray(d.CAD) ? d.CAD : [], USD: Array.isArray(d.USD) ? d.USD : [] };
    } catch { return { CAD: [], USD: [] }; }
}

function write(d) {
    try { localStorage.setItem(KEY, JSON.stringify(d)); } catch { /* ignore */ }
    try { window.dispatchEvent(new Event(EVT)); } catch { /* ignore */ }
}

export function useStoreCart() {
    const [cart, setCart] = useState(read);
    useEffect(() => {
        const on = () => setCart(read());
        window.addEventListener(EVT, on);
        window.addEventListener("storage", on);
        return () => { window.removeEventListener(EVT, on); window.removeEventListener("storage", on); };
    }, []);

    const add = useCallback((cur, product, value, price, qty = 1) => {
        const d = read();
        const list = d[cur] || [];
        const i = list.findIndex((x) => x.product_id === product.id && Number(x.value) === Number(value));
        if (i >= 0) list[i] = { ...list[i], qty: Math.min(10, list[i].qty + qty), price };
        else list.push({ product_id: product.id, name: product.name, brand: product.brand, color: product.color, image_url: product.image_url, value: Number(value), price: Number(price), qty: Math.min(10, qty) });
        d[cur] = list.slice(0, 10);
        write(d);
    }, []);

    const setQty = useCallback((cur, idx, qty) => {
        const d = read();
        if (!d[cur][idx]) return;
        if (qty <= 0) d[cur].splice(idx, 1);
        else d[cur][idx].qty = Math.min(10, qty);
        write(d);
    }, []);

    const clear = useCallback((cur) => { const d = read(); d[cur] = []; write(d); }, []);

    const count = (cart.CAD || []).reduce((s, x) => s + x.qty, 0) + (cart.USD || []).reduce((s, x) => s + x.qty, 0);
    return { cart, add, setQty, clear, count };
}

// Pictures uploaded in admin are served by the API server; full https links are used as they are
export function imgUrl(src) {
    if (!src) return null;
    return src.startsWith("/api/") ? API.replace(/\/api$/, "") + src : src;
}

// Card pictures shipped with the site (frontend/public/cards). Used when a product has no uploaded picture.
const BRAND_IMAGES = [
    [/steam/i, "/cards/steam.webp"],
    [/playstation|psn/i, "/cards/playstation.webp"],
    [/amazon/i, "/cards/amazon.webp"],
];
export function brandImage(brand, name) {
    const s = `${brand || ""} ${name || ""}`;
    const hit = BRAND_IMAGES.find(([re]) => re.test(s));
    return hit ? hit[1] : null;
}

function initials(s) {
    return (s || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

// A gift-card-shaped tile in the brand colour. No logos are drawn; an image is used only if one was added in admin.
export function CardTile({ brand, name, color = "#047857", image, value, cur, size = "md", className = "" }) {
    const big = size === "lg";
    const small = size === "sm";
    const pic = image || brandImage(brand, name);
    return (
        <div className={`relative w-full aspect-[1.586] overflow-hidden ${small ? "rounded-xl" : "rounded-2xl"} shadow-sm ${className}`}
            style={{ background: pic ? "transparent" : `linear-gradient(135deg, ${color} 0%, ${color} 45%, rgba(0,0,0,0.55) 140%)` }}>
            {!pic && <div className="absolute -right-8 -top-10 w-32 h-32 rounded-full bg-white/10" />}
            {!pic && <div className="absolute -left-10 -bottom-14 w-36 h-36 rounded-full bg-black/10" />}
            {pic ? (
                <img src={imgUrl(pic)} alt={`${brand || name || ""} gift card`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
                <>
                    <div className={`absolute ${small ? "left-2.5 top-2" : "left-4 top-3.5"} flex items-center gap-2`}>
                        <span className={`${small ? "w-6 h-6 text-[10px]" : "w-8 h-8 text-xs"} rounded-lg bg-white/20 text-white font-extrabold flex items-center justify-center backdrop-blur`}>{initials(brand || name)}</span>
                    </div>
                    <div className={`absolute ${small ? "left-2.5 bottom-2" : "left-4 bottom-3.5"} right-3 text-white`}>
                        <div className={`font-extrabold leading-tight truncate ${big ? "text-2xl" : small ? "text-xs" : "text-base"}`} style={{ fontFamily: "Outfit, ui-sans-serif, system-ui" }}>{brand || name}</div>
                        {!small && <div className="text-[11px] text-white/70 tracking-wide">GIFT CARD</div>}
                    </div>
                </>
            )}
            {value != null && (
                <div className={`absolute ${small ? "right-2 top-2 text-[10px] px-1.5 py-0.5" : "right-3 top-3 text-xs px-2 py-1"} rounded-full bg-white/90 text-slate-900 font-bold`}>
                    {shortMoney(cur, value)}
                </div>
            )}
        </div>
    );
}

export const STATUS = {
    pending_payment: { label: "Waiting for payment", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
    checking: { label: "Checking your payment", cls: "bg-sky-50 text-sky-700 ring-sky-200" },
    review: { label: "Security check", cls: "bg-violet-50 text-violet-700 ring-violet-200" },
    paid: { label: "Paid", cls: "bg-sky-50 text-sky-700 ring-sky-200" },
    awaiting_stock: { label: "Preparing your code", cls: "bg-sky-50 text-sky-700 ring-sky-200" },
    completed: { label: "Delivered", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600 ring-slate-200" },
    expired: { label: "Expired", cls: "bg-slate-100 text-slate-600 ring-slate-200" },
    refunded: { label: "Refunded", cls: "bg-rose-50 text-rose-700 ring-rose-200" },
};

export function StatusPill({ status }) {
    const s = STATUS[status] || { label: status || "-", cls: "bg-slate-100 text-slate-600 ring-slate-200" };
    return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${s.cls}`}>{s.label}</span>;
}

export const METHOD_LABEL = { wallet: "Wallet", card: "Card", paypal: "PayPal", etransfer: "Interac e-Transfer" };

// ---------- add-to-cart animation + cart dock ----------
const BUMP = "g2c-cart-bump";
const reduceMotion = () => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
};

// Flies a copy of the card (fromEl) into the cart dock, then makes the dock bounce.
export function flyToCart(fromEl) {
    const target = document.getElementById("g2c-cart-dock");
    const done = () => { try { window.dispatchEvent(new Event(BUMP)); } catch { /* ignore */ } };
    if (!fromEl || !target || reduceMotion() || !fromEl.animate) { setTimeout(done, 50); return; }
    const a = fromEl.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const ghost = fromEl.cloneNode(true);
    Object.assign(ghost.style, {
        position: "fixed", left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px`,
        margin: "0", zIndex: "9999", pointerEvents: "none", borderRadius: "16px", overflow: "hidden",
        boxShadow: "0 20px 40px -12px rgba(0,0,0,0.45)",
    });
    document.body.appendChild(ghost);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    const s = Math.max(0.08, 44 / a.width);
    const anim = ghost.animate([
        { transform: "translate(0,0) scale(1) rotate(0deg)", opacity: 1 },
        { transform: `translate(${dx * 0.45}px, ${dy * 0.25 - 80}px) scale(0.6) rotate(-8deg)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) scale(${s}) rotate(4deg)`, opacity: 0.4 },
    ], { duration: 750, easing: "cubic-bezier(.5,0,.3,1)" });
    anim.onfinish = () => { ghost.remove(); done(); };
}

// Always-visible cart at the bottom of the store pages
export function CartDock({ cur, to }) {
    const { cart, count } = useStoreCart();
    const [bump, setBump] = useState(false);
    useEffect(() => {
        const on = () => { setBump(true); setTimeout(() => setBump(false), 650); };
        window.addEventListener(BUMP, on);
        return () => window.removeEventListener(BUMP, on);
    }, []);
    const items = [...(cart[cur] || []), ...(cart[cur === "CAD" ? "USD" : "CAD"] || [])];
    const total = (cart[cur] || []).reduce((s2, x) => s2 + x.price * x.qty, 0);
    return (
        <Link to={to} id="g2c-cart-dock" aria-label={`Cart, ${count} item${count === 1 ? "" : "s"}`}
            className={`fixed z-40 left-1/2 -translate-x-1/2 bottom-4 sm:bottom-6 flex items-center gap-3 pl-3 pr-2 h-16 rounded-2xl bg-slate-900 text-white shadow-[0_18px_40px_-12px_rgba(15,23,42,0.6)] ring-1 ring-white/10 transition-all duration-300 ${count ? "opacity-100 translate-y-0" : "opacity-0 translate-y-24 pointer-events-none"} ${bump ? "scale-110" : "scale-100"}`}
            style={{ transitionProperty: "transform, opacity" }}>
            <span className="relative flex items-center">
                {items.slice(0, 3).map((x, i) => (
                    <span key={`${x.product_id}-${x.value}`} className="w-12 -ml-5 first:ml-0 rounded-md ring-2 ring-slate-900 shadow" style={{ transform: `rotate(${(i - 1) * 6}deg)` }}>
                        <CardTile brand={x.brand} name={x.name} color={x.color} image={x.image_url} size="sm" />
                    </span>
                ))}
                {!items.length && <span className="w-10 h-10 rounded-xl bg-white/10" />}
                <span className={`absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 rounded-full bg-emerald-400 text-emerald-950 text-xs font-extrabold flex items-center justify-center ${bump ? "animate-bounce" : ""}`}>{count}</span>
            </span>
            <span className="leading-tight">
                <span className="block text-[11px] text-slate-400">Your cart</span>
                <span className="block font-bold whitespace-nowrap">{money(cur, total)}</span>
            </span>
            <span className="h-11 px-4 rounded-xl bg-emerald-500 text-emerald-950 font-extrabold flex items-center whitespace-nowrap">Checkout →</span>
        </Link>
    );
}
